/**
 * HUD — thin presentational overlay. Reads a plain snapshot; owns no game logic.
 * Pointer-events are disabled on the container and re-enabled on controls so
 * drags/pinches fall through to the canvas everywhere except the buttons.
 */
import { useEffect, useState } from "react";
import type { HudSnapshot } from "@/engine/GameState";
import { QUEST_COUNT } from "@/game/quests/questline";
import UpgradeTree from "./UpgradeTree";
import StatsPanel from "./StatsPanel";
import InventoryPanel from "./InventoryPanel";
import { itemIcon, itemName } from "./itemIcons";
import PlanetServicesMenu from "./PlanetServicesMenu";
import TradeMenu from "./TradeMenu";
import CraftingPanel from "./CraftingPanel";
import ArsenalPanel from "./ArsenalPanel";
import ShieldPanel from "./ShieldPanel";
import TavernPanel from "./TavernPanel";
import MkUpgradePanel from "./MkUpgradePanel";
import Minimap from "./Minimap";
import type { Modification } from "@/game/mods/modifications";
import { t, useLang, toggleLang } from "@/i18n";
import "./hud.css";

interface Props {
  snap: HudSnapshot | null;
  onPurchaseUpgrade: (id: string) => void;
  onWarp: () => void;
  onExit: () => void;
  onPauseChange: (paused: boolean) => void;
  onToggleRange: () => void;
  onToggleMining: () => void;
  onToggleAutoMine: () => void;
  onToggleDrones: () => void;
  onToggleCannons: () => void;
  onToggleMechDrones: () => void;
  onAimCannons: () => void;
  onFireCannons: () => void;
  onLaunchMissiles: () => void;
  onFireWormBeam: () => void;
  onFireWormMissiles: () => void;
  onArmRift: () => void;
  onRiftTarget: (worldX: number, worldY: number) => void;
  onWaypoint: (worldX: number, worldY: number) => void;
  onFireCoil: () => void;
  onFireRiftSwarm: () => void;
  cannonAiming: boolean;
  onTrade: (units: number) => void;
  onBuyMarket: (id: string) => void;
  onRepair: () => void;
  onEquipShieldMod: (id: string) => void;
  onUnequipShieldMod: () => void;
  onUpgradeShieldMod: (id: string) => void;
  onBuildMod: () => Modification | null;
  onSellMod: (id: string) => void;
  onSellItem: (id: string) => void;
  onClearNotice: () => void;
  onClearBossResult: () => void;
  onCraft: (recipeId: string) => void;
  onBuyTurret: () => void;
  onUnlockSlot: (slot: number) => void;
  onEquipTurret: (slot: number) => void;
  onUnequipTurret: (slot: number) => void;
  onBuyCannon: () => void;
  onUnlockCannonSlot: (slot: number) => void;
  onEquipCannon: (slot: number) => void;
  onUnequipCannon: (slot: number) => void;
  onUnlockBigCannonSlot: (slot: number) => void;
  onEquipBigCannon: (slot: number) => void;
  onUnequipBigCannon: (slot: number) => void;
  onBuyFighter: (slot: number) => void;
  onDismissFighter: (slot: number) => void;
  onAssignPilot: (slot: number, crewId: string) => void;
  onUnassignPilot: (slot: number) => void;
  onCallFormation: () => void;
  onRecruit: (type: "mdc" | "ftp" | "rdc" | "gc" | "fdc") => void;
  onUpgradeCrew: (id: string) => void;
  onRerollCrew: (id: string) => void;
  onEquipMdc: (id: string) => void;
  onUnequipMdc: () => void;
  onEquipRdc: (id: string) => void;
  onUnequipRdc: () => void;
  onEquipGc: (id: string) => void;
  onUnequipGc: () => void;
  onEquipFdc: (id: string) => void;
  onUnequipFdc: () => void;
  onMarkInventorySeen: () => void;
  onRespawn: () => void;
  onSkipTutorial: () => void;
  onAdvanceDialog: () => void;
  onReset: () => void;
  onBackToMenu: () => void;
  onAdminSetLevel: (n: number) => void;
  onAdminAddSkill: (n: number) => void;
  onAdminAddMinerals: (n: number) => void;
  onAdminAddUnits: (n: number) => void;
  onAdminSetWarps: (n: number) => void;
  onAdminSetMk: (tier: number, path: "battleship" | "swarmlord") => void;
  onAdminAddItem: (id: string, n: number) => void;
  onAdminSpawnEater: () => void;
  onAdminSpawnStation: () => void;
  onAdminUnlockWorm: (mk: number) => void;
  onAdminGetAllUpgrades: () => void;
}

export default function HUD({
  snap,
  onPurchaseUpgrade,
  onWarp,
  onExit,
  onPauseChange,
  onToggleRange,
  onToggleMining,
  onToggleAutoMine,
  onToggleDrones,
  onToggleCannons,
  onToggleMechDrones,
  onAimCannons,
  onFireCannons,
  onLaunchMissiles,
  onFireWormBeam,
  onFireWormMissiles,
  onArmRift,
  onRiftTarget,
  onWaypoint,
  onFireCoil,
  onFireRiftSwarm,
  cannonAiming,
  onTrade,
  onBuyMarket,
  onRepair,
  onEquipShieldMod,
  onUnequipShieldMod,
  onUpgradeShieldMod,
  onBuildMod,
  onSellMod,
  onSellItem,
  onClearNotice,
  onClearBossResult,
  onCraft,
  onBuyTurret,
  onUnlockSlot,
  onEquipTurret,
  onUnequipTurret,
  onBuyCannon,
  onUnlockCannonSlot,
  onEquipCannon,
  onUnequipCannon,
  onUnlockBigCannonSlot,
  onEquipBigCannon,
  onUnequipBigCannon,
  onBuyFighter,
  onDismissFighter,
  onAssignPilot,
  onUnassignPilot,
  onCallFormation,
  onRecruit,
  onUpgradeCrew,
  onRerollCrew,
  onEquipMdc,
  onUnequipMdc,
  onEquipRdc,
  onUnequipRdc,
  onEquipGc,
  onUnequipGc,
  onEquipFdc,
  onUnequipFdc,
  onMarkInventorySeen,
  onRespawn,
  onSkipTutorial,
  onAdvanceDialog,
  onReset,
  onBackToMenu,
  onAdminSetLevel,
  onAdminAddSkill,
  onAdminAddMinerals,
  onAdminAddUnits,
  onAdminSetWarps,
  onAdminSetMk,
  onAdminAddItem,
  onAdminSpawnEater,
  onAdminSpawnStation,
  onAdminUnlockWorm,
  onAdminGetAllUpgrades,
}: Props) {
  const lang = useLang(); // re-render on language switch
  const dialogLine = snap?.quest?.dialogLine ?? null; // active Tutor line (pauses sim)
  const upgradeHint = snap?.tutorial?.highlightUpgrade ?? null; // quest-driven Upgrades pulse
  const [upgradesOpen, setUpgradesOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [craftOpen, setCraftOpen] = useState(false);
  const [arsenalOpen, setArsenalOpen] = useState(false);
  const [shieldOpen, setShieldOpen] = useState(false);
  const [warpConfirm, setWarpConfirm] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [admLevel, setAdmLevel] = useState("10");
  const [admSp, setAdmSp] = useState("10");
  const [admMin, setAdmMin] = useState("100000");
  const [admUnits, setAdmUnits] = useState("10000");
  const [admWarp, setAdmWarp] = useState("7");
  const [admMk, setAdmMk] = useState("bts");
  const [admItem, setAdmItem] = useState("energyCore");
  const [admItemN, setAdmItemN] = useState("10");
  const [tavernOpen, setTavernOpen] = useState(false);
  const [refitOpen, setRefitOpen] = useState(false);

  const anyModalOpen =
    upgradesOpen ||
    statsOpen ||
    invOpen ||
    servicesOpen ||
    tradeOpen ||
    craftOpen ||
    arsenalOpen ||
    shieldOpen ||
    tavernOpen ||
    refitOpen ||
    menuOpen ||
    warpConfirm;

  // Freeze the sim while the upgrade menu is open, or while a Tutor dialog is up.
  useEffect(() => {
    onPauseChange(upgradesOpen || dialogLine !== null);
  }, [upgradesOpen, dialogLine, onPauseChange]);

  if (!snap) return null;
  const dronesUnlocked = snap.hangarLevel > 0;
  const fightersUnlocked = snap.fleet.slots > 0;
  const xpPct =
    snap.expToNext > 0
      ? Math.max(0, Math.min(100, (snap.exp / snap.expToNext) * 100))
      : 0;

  return (
    <div className="hud">
      <Minimap data={snap.minimap} onTarget={onRiftTarget} onWaypoint={onWaypoint} />
      <button
        className="hud-menu-btn"
        onClick={() => setMenuOpen((v) => !v)}
        aria-label="Menu"
      >
        ☰
      </button>
      {menuOpen && (
        <>
          <div className="hud-menu-scrim" onClick={() => setMenuOpen(false)} />
          <div className="hud-menu" role="menu">
            <div className="hud-menu-lang">
              <span>🌐 {t("Language")}</span>
              <div className="hud-lang-toggle">
                <button
                  className={lang === "en" ? "on" : ""}
                  onClick={() => toggleLang()}
                >
                  EN
                </button>
                <button
                  className={lang === "th" ? "on" : ""}
                  onClick={() => toggleLang()}
                >
                  ไทย
                </button>
              </div>
            </div>
            {snap.isAdmin && (
              <div className="hud-admin">
                <div className="hud-admin-title">🛠 ADMINZ</div>
                <label className="hud-admin-row">
                  <span>Level</span>
                  <input
                    type="number"
                    value={admLevel}
                    onChange={(e) => setAdmLevel(e.target.value)}
                  />
                  <button onClick={() => onAdminSetLevel(Number(admLevel) || 1)}>Set</button>
                </label>
                <label className="hud-admin-row">
                  <span>+ Skill</span>
                  <input
                    type="number"
                    value={admSp}
                    onChange={(e) => setAdmSp(e.target.value)}
                  />
                  <button onClick={() => onAdminAddSkill(Number(admSp) || 0)}>Add</button>
                </label>
                <label className="hud-admin-row">
                  <span>+ Minerals</span>
                  <input
                    type="number"
                    value={admMin}
                    onChange={(e) => setAdmMin(e.target.value)}
                  />
                  <button onClick={() => onAdminAddMinerals(Number(admMin) || 0)}>Add</button>
                </label>
                <label className="hud-admin-row">
                  <span>+ Units</span>
                  <input
                    type="number"
                    value={admUnits}
                    onChange={(e) => setAdmUnits(e.target.value)}
                  />
                  <button onClick={() => onAdminAddUnits(Number(admUnits) || 0)}>Add</button>
                </label>
                <label className="hud-admin-row">
                  <span>Jump #</span>
                  <input
                    type="number"
                    value={admWarp}
                    onChange={(e) => setAdmWarp(e.target.value)}
                  />
                  <button onClick={() => onAdminSetWarps(Number(admWarp) || 0)}>Set</button>
                </label>
                <label className="hud-admin-row">
                  <span>Hull</span>
                  <select value={admMk} onChange={(e) => setAdmMk(e.target.value)}>
                    <option value="0">Miner Mk I</option>
                    <option value="1">Carrier Mk II</option>
                    <option value="2">Carrier Mk III</option>
                    <option value="3">Carrier Mk IV</option>
                    <option value="bts">Battleship Mk V</option>
                    <option value="swl">Swarm-lord Mk V</option>
                    <option value="bts6">Battleship Mk VI</option>
                    <option value="swl6">Swarm-lord Mk VI</option>
                  </select>
                  <button
                    onClick={() =>
                      admMk === "bts"
                        ? onAdminSetMk(4, "battleship")
                        : admMk === "swl"
                          ? onAdminSetMk(4, "swarmlord")
                          : admMk === "bts6"
                            ? onAdminSetMk(5, "battleship")
                            : admMk === "swl6"
                              ? onAdminSetMk(5, "swarmlord")
                              : onAdminSetMk(Number(admMk), "battleship")
                    }
                  >
                    Set
                  </button>
                </label>
                <label className="hud-admin-row">
                  <span>Item</span>
                  <select value={admItem} onChange={(e) => setAdmItem(e.target.value)}>
                    <option value="moonCore">Moon Core</option>
                    <option value="energyCore">Energy Core</option>
                    <option value="darkMatter">Dark Matter</option>
                    <option value="warpFuel">Warp Fuel</option>
                    <option value="turret_basic">Twin Cannon (turret)</option>
                    <option value="cannon_basic">Spinal Cannon</option>
                    <option value="eowHead">EOW Head (Mk VII)</option>
                    <option value="eowBody">EOW Segment (Mk VII)</option>
                    <option value="eowTail">EOW Tail (Mk VII)</option>
                    <option value="eowEnergyCore">EOW Core (Mk VII)</option>
                  </select>
                  <input
                    type="number"
                    value={admItemN}
                    onChange={(e) => setAdmItemN(e.target.value)}
                  />
                  <button onClick={() => onAdminAddItem(admItem, Number(admItemN) || 0)}>
                    Add
                  </button>
                </label>
                <label className="hud-admin-row">
                  <span>Boss</span>
                  <button onClick={() => onAdminSpawnEater()}>🐛 Spawn Eater of Worlds</button>
                </label>
                <label className="hud-admin-row">
                  <span>Station</span>
                  <button onClick={() => onAdminSpawnStation()}>🛰 Toggle Ring Station</button>
                </label>
                <label className="hud-admin-row">
                  <span>Worm</span>
                  <button onClick={() => onAdminUnlockWorm(7)}>🐛 Unlock Mk VII EOW</button>
                  <button onClick={() => onAdminUnlockWorm(8)}>🔷 Unlock Mk VIII + Rift</button>
                </label>
                <label className="hud-admin-row">
                  <span>Upgrades</span>
                  <button onClick={() => onAdminGetAllUpgrades()}>⚡ Get all (current path)</button>
                </label>
              </div>
            )}
            <button
              className="hud-menu-item"
              onClick={() => {
                setMenuOpen(false);
                onBackToMenu();
              }}
            >
              ⌂ {t("Main menu")}
            </button>
            <button
              className="hud-menu-item danger"
              onClick={() => {
                setMenuOpen(false);
                if (window.confirm(t("Reset ALL progress? This cannot be undone.")))
                  onReset();
              }}
            >
              ⟲ {t("Reset progress")}
            </button>
          </div>
        </>
      )}

      {snap.playerDead && (
        <div className="hud-death">
          <div className="hud-death-card">
            <div className="hud-death-boom">💥</div>
            <div className="hud-death-title">{t("SHIP DESTROYED")}</div>
            <p className="hud-death-body">
              {t(
                "Your Carrier was lost. Rebuild and jump back to the system — your upgrades, crew and cargo bays remain.",
              )}
            </p>
            <button className="hud-btn accent hud-death-btn" onClick={onRespawn}>
              🚀 {t("Respawn")}
            </button>
          </div>
        </div>
      )}
      {snap.bossResult && (
        <div className="hud-death" onClick={onClearBossResult}>
          <div className="hud-death-card hud-boss-card" onClick={(e) => e.stopPropagation()}>
            <div className="hud-death-boom">🏆</div>
            <div className="hud-death-title">{t("EATER OF WORLDS DEFEATED")}</div>
            <div className="hud-boss-rewards">
              <span className="hud-boss-stat">✦ {t("EXP")} +{snap.bossResult.xp}</span>
              <span className="hud-boss-stat">◎ {t("Units")} +{snap.bossResult.units}</span>
            </div>
            {snap.bossResult.drops.length > 0 ? (
              <div className="hud-boss-drops">
                <div className="hud-boss-drops-title">{t("Salvage recovered")}</div>
                <div className="hud-boss-drops-grid">
                  {snap.bossResult.drops.map((d, i) => {
                    const url = itemIcon(d.id);
                    return (
                      <div className="hud-boss-drop" key={i}>
                        {url ? (
                          <img className="hud-boss-drop-img" src={url} alt="" />
                        ) : (
                          <span className="hud-boss-drop-emoji">◈</span>
                        )}
                        <span className="hud-boss-drop-name">
                          {t(itemName(d.id))}
                          {d.count > 1 ? ` ×${d.count}` : ""}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="hud-boss-nodrops">{t("No salvage recovered this time.")}</div>
            )}
            <button className="hud-btn accent hud-death-btn" onClick={onClearBossResult}>
              {t("Claim")}
            </button>
          </div>
        </div>
      )}
      {snap.notice && (
        <button className="hud-toast" onClick={onClearNotice}>
          {snap.notice} <span className="hud-toast-x">✕</span>
        </button>
      )}
      <div className="hud-panel hud-top">
        <div className="hud-title">
          <span className="tier">Lv {snap.level}</span> {snap.shipName}
          {snap.skillPoints > 0 && (
            <span className="sp-badge">★ {snap.skillPoints} SP</span>
          )}
        </div>
        <div className="xp-bar" title={`${Math.floor(snap.exp)} / ${snap.expToNext} XP`}>
          <div className="xp-fill" style={{ width: `${xpPct}%` }} />
          <span className="xp-text">
            {Math.floor(snap.exp)} / {snap.expToNext} XP
          </span>
        </div>
        <div className="vitals">
          <VBar label={t("⛉ Shield")} value={snap.shield} max={snap.maxShield} cls="shield" />
          <VBar label={t("❤ Hull")} value={snap.hp} max={snap.maxHp} cls="hull" />
        </div>
        <div className="hud-stats">
          <Stat label={t("Minerals")} value={fmt(snap.minerals)} accent="#9fe6ff" />
          <Stat label={t("Units")} value={fmt(snap.units)} accent="#ffd27a" />
          {dronesUnlocked ? (
            <Stat
              label={t("Drones")}
              value={`${snap.orbitingDrones}/${snap.hangarLevel} ${t("mining")}`}
            />
          ) : (
            <Stat label={t("Drones")} value={t("Locked")} accent="#6b7a90" />
          )}
          {fightersUnlocked && (
            <Stat
              label={t("Fighters")}
              value={`${snap.fleet.deployed}/${snap.fleet.ready} ${t("flying")}`}
            />
          )}
          <Stat
            label={t(snap.inPlanetLayer ? "Bodies" : "Planets")}
            value={fmt(snap.bodiesRemaining)}
          />
          <Stat label={t("Map")} value={t(snap.layerLabel)} accent="#8fd0ff" />
          <Stat label={t("Jumps")} value={`${snap.warps}`} accent="#caa2ff" />
          {snap.darkMatter > 0 && (
            <Stat label={t("Dark Matter")} value={fmt(snap.darkMatter)} accent="#caa2ff" />
          )}
        </div>
      </div>

      {snap.inPlanetLayer && (
        <div className="hud-left">
          <button
            className={`hud-toggle${snap.hullMiningEnabled && !snap.inCombat ? " on" : " off"}`}
            onClick={onToggleMining}
            title={snap.inCombat ? "Mining suspended in combat" : "Toggle hull mining"}
          >
            ⛏
          </button>
          <button
            className={`hud-toggle${snap.autoMineEnabled && !snap.inCombat ? " on" : " off"}`}
            onClick={onToggleAutoMine}
            title={t(snap.inCombat ? "Auto-mine paused in combat" : "Auto-mine: fly to ore & mine, seek the next")}
          >
            🤖
          </button>
          <button
            className={`hud-toggle${snap.dronesEnabled && !snap.inCombat ? " on" : " off"}`}
            onClick={onToggleDrones}
            title={snap.inCombat ? "Drones suspended in combat" : "Toggle drones"}
          >
            ⚙
          </button>
          {snap.arsenal.hullTier >= 1 && (
            <button
              className={`hud-toggle${snap.cannonsEnabled ? " on" : " off"}`}
              onClick={onToggleCannons}
              title="Toggle cannons"
            >
              🔫
            </button>
          )}
          {snap.mechDronesUnlocked && (
            <button
              className={`hud-toggle${snap.mechDronesEnabled && !snap.inCombat ? " on" : " off"}`}
              onClick={onToggleMechDrones}
              title={
                snap.inCombat
                  ? "Repair drones suspended in combat"
                  : "Toggle repair drones"
              }
            >
              🔧
            </button>
          )}
        </div>
      )}

      {snap.inPlanetLayer && (snap.missileSkill.available || snap.riftSwarm.show) && (
        <div className="hud-skill-rail">
          {snap.missileSkill.available && (
            <button
              className="hud-skill-btn"
              onClick={onLaunchMissiles}
              disabled={!snap.missileSkill.ready}
              title={t("Launch a homing-missile salvo")}
            >
              {!snap.missileSkill.ready && (
                <span
                  className="hud-skill-cd"
                  style={{ height: `${snap.missileSkill.cooldownFrac * 100}%` }}
                />
              )}
              <span className="hud-skill-icon">🚀</span>
              <span className="hud-skill-count">×{snap.missileSkill.count}</span>
            </button>
          )}
          {snap.riftSwarm.show && (
            <button
              className="hud-skill-btn"
              onClick={onFireRiftSwarm}
              disabled={!snap.riftSwarm.ready}
              title={t("Rift Swarm — a volley of piercing homing arrows from two rifts")}
            >
              {snap.riftSwarm.cooldownFrac > 0 && (
                <span
                  className="hud-skill-cd"
                  style={{ height: `${snap.riftSwarm.cooldownFrac * 100}%` }}
                />
              )}
              <span className="hud-skill-icon">🌌</span>
            </button>
          )}
        </div>
      )}

      {snap.inPlanetLayer && snap.wormBeam.active && (
        <div className="hud-skill-rail">
          <button
            className="hud-skill-btn"
            onClick={onFireWormBeam}
            disabled={!snap.wormBeam.ready}
            title={t("Charge and fire the maw beam (splits into a fan)")}
          >
            {snap.wormBeam.busyFrac > 0 && (
              <span
                className="hud-skill-cd"
                style={{ height: `${snap.wormBeam.busyFrac * 100}%` }}
              />
            )}
            <span className="hud-skill-icon">⚡</span>
          </button>
          {snap.wormMissile.active && (
            <button
              className="hud-skill-btn"
              onClick={onFireWormMissiles}
              disabled={!snap.wormMissile.ready}
              title={t("Launch the red homing-missile salvo")}
            >
              {snap.wormMissile.cooldownFrac > 0 && (
                <span
                  className="hud-skill-cd"
                  style={{ height: `${snap.wormMissile.cooldownFrac * 100}%` }}
                />
              )}
              <span className="hud-skill-icon">🚀</span>
              <span className="hud-skill-count">×{snap.wormMissile.count}</span>
            </button>
          )}
          {snap.rift.active && (
            <button
              className="hud-skill-btn"
              onClick={onArmRift}
              disabled={!snap.rift.ready}
              title={t("Time & Space Rift — arm, then tap the minimap to teleport")}
              style={
                snap.rift.armed ? { boxShadow: "0 0 0 2px #d0a3ff, 0 0 14px #b98cff" } : undefined
              }
            >
              {snap.rift.cooldownFrac > 0 && (
                <span
                  className="hud-skill-cd"
                  style={{ height: `${snap.rift.cooldownFrac * 100}%` }}
                />
              )}
              <span className="hud-skill-icon">🌀</span>
            </button>
          )}
          {snap.coil.active && (
            <button
              className="hud-skill-btn"
              onClick={onFireCoil}
              disabled={!snap.coil.ready}
              title={t("Maelstrom Coil — curl up: every segment sweeps a laser + a shield bubble")}
            >
              {snap.coil.cooldownFrac > 0 && (
                <span
                  className="hud-skill-cd"
                  style={{ height: `${snap.coil.cooldownFrac * 100}%` }}
                />
              )}
              <span className="hud-skill-icon">🌪️</span>
            </button>
          )}
        </div>
      )}

      {snap.inPlanetLayer && snap.cannonsReady && (
        <div className="hud-right">
          <button
            className={`hud-cannon${cannonAiming ? " on" : ""}${snap.cannonLocked ? " locked" : ""}`}
            onClick={onAimCannons}
            title="Aim spinal cannons, then tap an enemy, a moon, or open space"
          >
            🎯
          </button>
          <button
            className="hud-cannon fire"
            onClick={onFireCannons}
            disabled={!snap.cannonTargeted}
            title="Fire spinal cannons"
          >
            🔥
          </button>
          <div className="hud-cannon-mode">
            {snap.cannonLocked
              ? t("LOCKED")
              : cannonAiming
                ? t("tap target")
                : snap.inCombat
                  ? t("AUTO")
                  : ""}
          </div>
        </div>
      )}

      <div className="hud-panel hud-bottom">
        <div className="hud-hint">
          {snap.inCombat
            ? t(
                "⚔ In combat — mining lasers & drones are offline until the fighters are down",
              )
            : snap.inPlanetLayer &&
                snap.planetService !== "none" &&
                !snap.atServicePlanet
              ? snap.planetService === "mechanical"
                ? t("Fly up to the 🔧 mechanical planet to use its services")
                : snap.planetService === "tavern"
                  ? t("Fly up to the 🍺 Space Tavern to use its services")
                  : t("Fly up to the 🛒 trade-hub to use its services")
              : snap.inPlanetLayer
                ? snap.hasSelection
                  ? t(
                      "⛏ Flying in to mine — tap the target again to stop · tap space to fly",
                    )
                  : dronesUnlocked
                    ? t("Tap an asteroid to mine it · drones auto-mine nearby rocks")
                    : t(
                        "Tap an asteroid to fly over and mine it · tap empty space to move",
                      )
                : t(
                    "Tap a planet to travel to it · tap empty space to explore the system",
                  )}
        </div>
        {snap.inPlanetLayer && (
          <button className="hud-btn" onClick={onExit}>
            ◂ {t("Leave Planet")}
          </button>
        )}
        {snap.atServicePlanet && snap.planetService === "mechanical" && (
          <button className="hud-btn accent" onClick={() => setServicesOpen(true)}>
            🔧 {t("Services")}
          </button>
        )}
        {snap.atServicePlanet && snap.planetService === "trade" && (
          <button className="hud-btn accent" onClick={() => setTradeOpen(true)}>
            🛒 {t("Trade")}
          </button>
        )}
        {snap.atServicePlanet && snap.planetService === "tavern" && (
          <button className="hud-btn accent" onClick={() => setTavernOpen(true)}>
            🍺 {t("Tavern")}
          </button>
        )}
        <button
          className={`hud-btn accent${upgradeHint ? " tut-pulse" : ""}`}
          onClick={() => setUpgradesOpen(true)}
        >
          ⚙ {t("Upgrades")}
        </button>
        <button className="hud-btn ghost" onClick={() => setStatsOpen(true)}>
          📊 {t("Stats")}
        </button>
        <button className="hud-btn ghost inv-btn" onClick={() => setInvOpen(true)}>
          🎒 {t("Items")}
          {snap.newItemCount > 0 && (
            <span className="hud-badge">{snap.newItemCount}</span>
          )}
        </button>
        {snap.inPlanetLayer && (
          <button
            className={`hud-btn ghost${snap.showMiningRange ? " on" : ""}`}
            onClick={onToggleRange}
          >
            ◎ {t("Range")}
          </button>
        )}
        {snap.inPlanetLayer && snap.fleet.fighters.some((f) => f && f.pilot) && (
          <button
            className={`hud-btn ghost${snap.fleet.formationActive ? " on" : ""}`}
            onClick={onCallFormation}
          >
            ✈ {t("Formation")}
          </button>
        )}
        {!snap.inPlanetLayer && (
          <button
            className="hud-btn ghost"
            onClick={() => setWarpConfirm(true)}
            disabled={(snap.inventory.warpFuel ?? 0) < 1}
            title={t("Consumes 1 Warp Drive Fuel — engages from the solar layer")}
          >
            {t("Warp")} ▸ (⛽ {snap.inventory.warpFuel ?? 0})
          </button>
        )}
      </div>

      {snap.quest.step >= 1 &&
        snap.quest.step <= QUEST_COUNT &&
        !dialogLine &&
        !anyModalOpen && (
          <QuestTracker
            step={snap.quest.step}
            title={snap.quest.title}
            objective={snap.quest.objective}
            progress={snap.quest.progress}
          />
        )}

      {dialogLine && (
        <TutorDialog
          line={dialogLine}
          more={snap.quest.dialogMore}
          speaker={snap.quest.dialogSpeaker}
          onAdvance={onAdvanceDialog}
          onSkip={onSkipTutorial}
        />
      )}

      {upgradesOpen && (
        <UpgradeTree
          purchased={snap.upgrades}
          minerals={snap.minerals}
          skillPoints={snap.skillPoints}
          units={snap.units}
          energyCores={snap.inventory.energyCore ?? 0}
          inventory={snap.inventory}
          atMechanical={snap.planetService === "mechanical"}
          highlightId={snap.tutorial.highlightUpgrade}
          onPurchase={onPurchaseUpgrade}
          onClose={() => setUpgradesOpen(false)}
        />
      )}

      {statsOpen && <StatsPanel snap={snap} onClose={() => setStatsOpen(false)} />}

      {invOpen && (
        <InventoryPanel
          snap={snap}
          onSeen={onMarkInventorySeen}
          onAssignPilot={onAssignPilot}
          onUnassignPilot={onUnassignPilot}
          onClose={() => setInvOpen(false)}
        />
      )}

      {servicesOpen && (
        <PlanetServicesMenu
          snap={snap}
          onOpenUpgrades={() => {
            setServicesOpen(false);
            setUpgradesOpen(true);
          }}
          onRepair={onRepair}
          onOpenShield={() => {
            setServicesOpen(false);
            setShieldOpen(true);
          }}
          onOpenCrafting={() => {
            setServicesOpen(false);
            setCraftOpen(true);
          }}
          onOpenArsenal={() => {
            setServicesOpen(false);
            setArsenalOpen(true);
          }}
          onOpenRefit={() => {
            setServicesOpen(false);
            setRefitOpen(true);
          }}
          onClose={() => setServicesOpen(false)}
        />
      )}

      {refitOpen && (
        <MkUpgradePanel
          snap={snap}
          onRefit={(id) => {
            onPurchaseUpgrade(id);
          }}
          onClose={() => setRefitOpen(false)}
        />
      )}

      {arsenalOpen && (
        <ArsenalPanel
          snap={snap}
          onBuyTurret={onBuyTurret}
          onUnlockSlot={onUnlockSlot}
          onEquip={onEquipTurret}
          onUnequip={onUnequipTurret}
          onBuyCannon={onBuyCannon}
          onUnlockCannonSlot={onUnlockCannonSlot}
          onEquipCannon={onEquipCannon}
          onUnequipCannon={onUnequipCannon}
          onUnlockBigCannonSlot={onUnlockBigCannonSlot}
          onEquipBigCannon={onEquipBigCannon}
          onUnequipBigCannon={onUnequipBigCannon}
          onBuyFighter={onBuyFighter}
          onDismissFighter={onDismissFighter}
          onAssignPilot={onAssignPilot}
          onUnassignPilot={onUnassignPilot}
          onClose={() => setArsenalOpen(false)}
        />
      )}

      {tavernOpen && (
        <TavernPanel
          snap={snap}
          onRecruit={onRecruit}
          onUpgradeCrew={onUpgradeCrew}
          onRerollCrew={onRerollCrew}
          onEquipMdc={onEquipMdc}
          onUnequipMdc={onUnequipMdc}
          onEquipRdc={onEquipRdc}
          onUnequipRdc={onUnequipRdc}
          onEquipGc={onEquipGc}
          onUnequipGc={onUnequipGc}
          onEquipFdc={onEquipFdc}
          onUnequipFdc={onUnequipFdc}
          onClose={() => setTavernOpen(false)}
        />
      )}

      {shieldOpen && (
        <ShieldPanel
          snap={snap}
          onEquip={onEquipShieldMod}
          onUnequip={onUnequipShieldMod}
          onUpgrade={onUpgradeShieldMod}
          onClose={() => setShieldOpen(false)}
        />
      )}

      {tradeOpen && (
        <TradeMenu
          snap={snap}
          onTrade={onTrade}
          onBuyMarket={onBuyMarket}
          onBuildMod={onBuildMod}
          onSellMod={onSellMod}
          onSellItem={onSellItem}
          onClose={() => setTradeOpen(false)}
        />
      )}

      {craftOpen && (
        <CraftingPanel
          snap={snap}
          onCraft={onCraft}
          onClose={() => setCraftOpen(false)}
        />
      )}

      {warpConfirm && (
        <div className="modal-backdrop" onClick={() => setWarpConfirm(false)}>
          <div
            className="confirm-panel"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Confirm warp"
          >
            <div className="confirm-title">⚠ Leave this solar system?</div>
            <div className="confirm-text">
              Warping burns 1 Warp Drive Fuel and jumps to a brand-new solar
              system. You will <b>never be able to return</b> to this one.
            </div>
            <div className="confirm-actions">
              <button className="hud-btn ghost" onClick={() => setWarpConfirm(false)}>
                Cancel
              </button>
              <button
                className="hud-btn accent"
                onClick={() => {
                  setWarpConfirm(false);
                  onWarp();
                }}
              >
                Warp ▸
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Tutor speech overlay — click anywhere to advance to the next line. */
function TutorDialog({
  line,
  more,
  speaker,
  onAdvance,
  onSkip,
}: {
  line: string;
  more: boolean;
  speaker: "aria" | "eater";
  onAdvance: () => void;
  onSkip: () => void;
}) {
  useLang();
  const isEater = speaker === "eater";
  const portrait = isEater
    ? `${import.meta.env.BASE_URL}sprites/EOW_Head.png`
    : `${import.meta.env.BASE_URL}portraits/tutor.png`;
  const name = isEater ? t("The Eater of Worlds") : t("Aria");
  return (
    <div className="tutor-dialog-backdrop" onClick={onAdvance}>
      <div className={`tutor-dialog-wrap${isEater ? " tutor-dialog-eater" : ""}`}>
        <img className="tutor-dialog-portrait" src={portrait} alt={name} />
        <div className="tutor-dialog-box">
          <div className="tutor-dialog-name">{name}</div>
          <div className="tutor-dialog-text">{t(line)}</div>
          <div className="tutor-dialog-foot">
            <button
              className="tutor-dialog-skip"
              onClick={(e) => {
                e.stopPropagation();
                onSkip();
              }}
            >
              {t("Skip guide")}
            </button>
            <span className="tutor-dialog-hint">
              {more ? t("▸ tap to continue") : t("▸ tap to start")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Small persistent objective panel while a quest is active. */
function QuestTracker({
  step,
  title,
  objective,
  progress,
}: {
  step: number;
  title: string;
  objective: string;
  progress: string;
}) {
  useLang();
  return (
    <div className="quest-tracker">
      <div className="quest-tracker-head">
        <span className="quest-tracker-badge">
          {t("QUEST")} {step}/{QUEST_COUNT}
        </span>
        <span className="quest-tracker-title">{t(title)}</span>
      </div>
      <div className="quest-tracker-obj">▸ {t(objective)}</div>
      {progress && <div className="quest-tracker-prog">{progress}</div>}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={accent ? { color: accent } : undefined}>
        {value}
      </div>
    </div>
  );
}

function VBar({
  label,
  value,
  max,
  cls,
}: {
  label: string;
  value: number;
  max: number;
  cls: string;
}) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className={`vbar ${cls}`}>
      <div className="vbar-fill" style={{ width: `${pct}%` }} />
      <span className="vbar-text">
        {label} {Math.ceil(value)}/{Math.round(max)}
      </span>
    </div>
  );
}

function fmt(n: number): string {
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "k";
  return Math.floor(n).toString();
}
